"use client";

import { useState } from "react";
import { Plug, Plus, Trash2 } from "lucide-react";

import { BaseDialogFooter } from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import { Label } from "@/components/shadcn/label";
import { RadioGroup, RadioGroupItem } from "@/components/shadcn/radio-group";
import { Toggle } from "@/components/Form/ToggleField";
import { SpotToast } from "@/components/SpotToast";
import type {
  CustomMcpHeadersConnectRequest,
  CustomMcpHeadersConnectResponse,
  CustomMcpStartRequest,
  CustomMcpStartResponse,
} from "@/generated";
import apiClient from "@/services/api-client";

type HeaderRow = { id: string; key: string; value: string };

const newHeaderId = (): string => Math.random().toString(36).slice(2, 11);
type AuthMode = "oauth" | "headers";

export function CustomMCPConnectForm({
  groupId,
  returnTo,
  onBack,
}: {
  groupId: string;
  returnTo: string;
  onBack: () => void;
}) {
  const [displayName, setDisplayName] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [authMode, setAuthMode] = useState<AuthMode>("oauth");
  const [headers, setHeaders] = useState<HeaderRow[]>([]);
  const [sendIdentity, setSendIdentity] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const updateHeader = (index: number, patch: Partial<HeaderRow>) => {
    setHeaders((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };
  const addHeader = () =>
    setHeaders((prev) => [...prev, { id: newHeaderId(), key: "", value: "" }]);
  const removeHeader = (index: number) =>
    setHeaders((prev) => prev.filter((_, i) => i !== index));

  const collectHeaders = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const { key, value } of headers) {
      const k = key.trim();
      if (!k) continue;
      out[k] = value;
    }
    return out;
  };

  const submitOAuth = async (body: CustomMcpStartRequest): Promise<void> => {
    const resp = await apiClient.post<CustomMcpStartResponse>(
      `/api/backend/groups/${groupId}/integrations/mcp/custom/start`,
      body
    );
    window.location.href = resp.data.authorize_url;
  };

  const submitHeaders = async (
    body: CustomMcpHeadersConnectRequest
  ): Promise<void> => {
    await apiClient.post<CustomMcpHeadersConnectResponse>(
      `/api/backend/groups/${groupId}/integrations/mcp/custom/connect`,
      body
    );
    SpotToast.success("Connected.");
    window.location.href = returnTo;
  };

  const handleSubmit = async () => {
    const url = serverUrl.trim();
    const name = displayName.trim();
    if (!url || !name) {
      SpotToast.error("Server URL and display name are required");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      SpotToast.error("Server URL must start with http:// or https://");
      return;
    }

    const headerEntries = collectHeaders();
    if (
      authMode === "headers" &&
      !sendIdentity &&
      Object.keys(headerEntries).length === 0
    ) {
      SpotToast.error("Headers-only mode requires at least one header");
      return;
    }

    setSubmitting(true);
    try {
      if (authMode === "headers") {
        await submitHeaders({
          server_url: url,
          display_name: name,
          transport: "url",
          headers: headerEntries,
          send_identity: sendIdentity,
        });
      } else {
        await submitOAuth({
          server_url: url,
          display_name: name,
          transport: "url",
          redirect_uri: returnTo,
          headers: headerEntries,
          client_id: clientId.trim() || null,
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to connect to MCP server";
      SpotToast.error(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto thin-scrollbars px-6 py-4">
        <div className="flex items-start gap-3 pb-4 mb-4 border-b border-border">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-background border border-border/40 flex items-center justify-center">
            <Plug size={20} className="text-foreground" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground">Add custom MCP</span>
            <span className="text-sm text-muted-foreground">
              Connect any remote MCP server by URL.
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-display-name">Name</Label>
            <Input
              id="mcp-display-name"
              placeholder="My MCP server"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="mcp-server-url">Server URL</Label>
            <Input
              id="mcp-server-url"
              placeholder="https://mcp.example.com/mcp"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Authentication</Label>
            <RadioGroup
              value={authMode}
              onValueChange={(v) => setAuthMode(v as AuthMode)}
              className="gap-2"
            >
              <label className="flex items-start gap-2.5 cursor-pointer">
                <RadioGroupItem
                  value="oauth"
                  id="auth-oauth"
                  disabled={submitting}
                  className="mt-0.5"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">OAuth</span>
                  <span className="text-xs text-muted-foreground">
                    Discover OAuth metadata and run an authorization flow.
                  </span>
                </div>
              </label>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <RadioGroupItem
                  value="headers"
                  id="auth-headers"
                  disabled={submitting}
                  className="mt-0.5"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Headers only</span>
                  <span className="text-xs text-muted-foreground">
                    Skip OAuth: auth comes from the headers below (API key,
                    proxy auth, …).
                  </span>
                </div>
              </label>
            </RadioGroup>
          </div>

          {authMode === "oauth" && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="mcp-client-id">Client ID</Label>
                <span className="text-xs text-muted-foreground">Optional</span>
              </div>
              <Input
                id="mcp-client-id"
                placeholder="Pre-registered OAuth client ID"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                For servers that don&apos;t support automatic client
                registration (e.g. Salesforce): paste the client ID of an OAuth
                app registered with the provider.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label>
                {authMode === "headers" ? "Headers" : "Custom headers"}
              </Label>
              <span className="text-xs text-muted-foreground">
                {authMode === "headers"
                  ? "Sent on every request"
                  : "Sent on discovery and every request"}
              </span>
            </div>
            {headers.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {authMode === "headers"
                  ? "Add the headers your MCP server requires (e.g. Authorization)."
                  : "Optional. Add headers if your MCP server requires them (e.g. tenant routing, proxy auth)."}
              </p>
            )}
            {headers.map((row, idx) => (
              <div key={row.id} className="flex items-center gap-2">
                <Input
                  placeholder="Header"
                  value={row.key}
                  onChange={(e) => updateHeader(idx, { key: e.target.value })}
                  disabled={submitting}
                  className="flex-1"
                />
                <Input
                  placeholder="Value"
                  value={row.value}
                  onChange={(e) => updateHeader(idx, { value: e.target.value })}
                  disabled={submitting}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeHeader(idx)}
                  disabled={submitting}
                  aria-label="Remove header"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addHeader}
              disabled={submitting}
              className="self-start"
            >
              <Plus size={14} />
              Add header
            </Button>
          </div>

          {authMode === "headers" && (
            <Toggle
              name="mcp-send-identity"
              value={sendIdentity}
              onChange={setSendIdentity}
              label="Send who is chatting"
              description="Adds a signed Noxus token to every request with the person's Noxus user id and email, so your server knows who is asking. Your server checks it with our public keys."
              isDisabled={submitting}
            />
          )}
        </div>
      </div>

      <BaseDialogFooter
        footerActions={[
          {
            label: "Cancel",
            props: {
              variant: "outline",
              onClick: onBack,
              disabled: submitting,
            },
          },
          {
            label: submitting
              ? authMode === "headers"
                ? "Connecting…"
                : "Starting…"
              : authMode === "headers"
                ? "Connect"
                : "Continue",
            props: { onClick: handleSubmit, disabled: submitting },
          },
        ]}
      />
    </div>
  );
}
